import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Container,
    Grid,
    TextField,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Switch,
    Paper,
    FormHelperText
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { jobVacancyService } from '../services/jobVacancyService';
import { AvailablePosition, CreateVacancyData, UpdateVacancyData, VacancyStatus } from '../types/jobVacancy';
import { SelectChangeEvent } from '@mui/material/Select';

const workModels = [
    { value: 'REMOTE', label: 'Remoto' },
    { value: 'HYBRID', label: 'Híbrido' },
    { value: 'ON_SITE', label: 'Presencial' }
];

const contractTypes = [
    { value: 'CLT', label: 'CLT' },
    { value: 'PJ', label: 'PJ' },
    { value: 'TEMPORARY', label: 'Temporário' },
    { value: 'INTERNSHIP', label: 'Estágio' }
];

const seniorityLevels = [
    { value: 'INTERN', label: 'Estagiário' },
    { value: 'JUNIOR', label: 'Júnior' },
    { value: 'MID_LEVEL', label: 'Pleno' },
    { value: 'SENIOR', label: 'Sênior' },
    { value: 'SPECIALIST', label: 'Especialista' },
    { value: 'LEAD', label: 'Líder' }
];

export const VacancyEdit: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(false);
    const [positions, setPositions] = useState<AvailablePosition[]>([]);
    const [heading, setHeading] = useState('');
    const [formData, setFormData] = useState<CreateVacancyData | UpdateVacancyData>({
        project_position_id: 0,
        contract_type: '',
        base_salary: 0,
        description: '',
        requirements: '',
        benefits: '',
        location: '',
        work_model: '',
        seniority: '',
        is_public: false,
        status: VacancyStatus.DRAFT
    });

    useEffect(() => {
        if (id) {
            loadVacancy();
        } else {
            // A vaga nasce de uma posição de projeto ativo (antes o formulário não pedia e o backend recusava)
            jobVacancyService.getAvailablePositions()
                .then(setPositions)
                .catch(() => enqueueSnackbar('Erro ao carregar posições disponíveis', { variant: 'error' }));
        }
    }, [id]);

    const handlePositionChange = (event: SelectChangeEvent<string>) => {
        const position = positions.find((p) => p.id === Number(event.target.value));
        if (!position) return;
        setFormData((prev: any) => ({
            ...prev,
            project_position_id: position.id,
            contract_type: position.contract_type,
            base_salary: position.base_salary,
        }));
    };

    const loadVacancy = async () => {
        try {
            setLoading(true);
            const vacancy = await jobVacancyService.getVacancy(Number(id));
            setHeading([vacancy.role, vacancy.project_name].filter(Boolean).join(' · '));
            setFormData({
                contract_type: vacancy.contract_type,
                base_salary: vacancy.base_salary,
                description: vacancy.description || '',
                requirements: vacancy.requirements || '',
                benefits: vacancy.benefits || '',
                location: vacancy.location || '',
                work_model: vacancy.work_model || '',
                seniority: vacancy.seniority || '',
                is_public: vacancy.is_public
            });
        } catch (error) {
            enqueueSnackbar('Erro ao carregar vaga', { variant: 'error' });
            navigate('/vagas');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field: keyof (CreateVacancyData | UpdateVacancyData)) => (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
    ) => {
        const value = 'type' in event.target && event.target.type === 'checkbox'
            ? (event.target as HTMLInputElement).checked 
            : event.target.value;
        setFormData((prev: any) => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!id && !(formData as CreateVacancyData).project_position_id) {
            enqueueSnackbar('Selecione a posição do projeto', { variant: 'warning' });
            return;
        }
        const payload = { ...formData, base_salary: Number(formData.base_salary) || 0 };

        try {
            setLoading(true);
            if (id) {
                await jobVacancyService.updateVacancy(Number(id), payload as UpdateVacancyData);
                enqueueSnackbar('Vaga atualizada com sucesso', { variant: 'success' });
            } else {
                await jobVacancyService.createVacancy(payload as CreateVacancyData);
                enqueueSnackbar('Vaga criada com sucesso', { variant: 'success' });
            }
            navigate('/vagas');
        } catch (error: any) {
            enqueueSnackbar(error?.response?.data?.error || 'Erro ao salvar vaga', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container maxWidth="lg">
            <Box sx={{ py: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
                    {id ? 'Editar Vaga' : 'Nova Vaga'}
                </Typography>
                {heading && (
                    <Typography variant="subtitle1" color="text.secondary">{heading}</Typography>
                )}

                <Paper sx={{ p: { xs: 2, sm: 3 }, mt: 3 }}>
                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={3}>
                            {!id && (
                                <Grid item xs={12}>
                                    <FormControl fullWidth required>
                                        <InputLabel id="vacancy-position-label">Posição do projeto</InputLabel>
                                        <Select
                                            value={(formData as CreateVacancyData).project_position_id ? String((formData as CreateVacancyData).project_position_id) : ''}
                                            label="Posição do projeto"
                                        labelId="vacancy-position-label"
                                            onChange={handlePositionChange}
                                        >
                                            {positions.map((position) => (
                                                <MenuItem key={position.id} value={String(position.id)}>
                                                    {position.role} — {position.project_name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {positions.length === 0 && (
                                            <FormHelperText>
                                                Nenhuma posição planejada em projetos ativos. As vagas nascem das posições da simulação aprovada.
                                            </FormHelperText>
                                        )}
                                    </FormControl>
                                </Grid>
                            )}
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel id="vacancy-contract-label">Tipo de Contrato</InputLabel>
                                    <Select
                                        value={formData.contract_type}
                                        label="Tipo de Contrato"
                                        labelId="vacancy-contract-label"
                                        onChange={handleChange('contract_type')}
                                        required
                                    >
                                        {contractTypes.map((type) => (
                                            <MenuItem key={type.value} value={type.value}>
                                                {type.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Salário Base"
                                    type="number"
                                    value={formData.base_salary}
                                    onChange={handleChange('base_salary')}
                                    required
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel id="vacancy-workmodel-label">Modelo de Trabalho</InputLabel>
                                    <Select
                                        value={formData.work_model}
                                        label="Modelo de Trabalho"
                                        labelId="vacancy-workmodel-label"
                                        onChange={handleChange('work_model')}
                                    >
                                        {workModels.map((model) => (
                                            <MenuItem key={model.value} value={model.value}>
                                                {model.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel id="vacancy-seniority-label">Senioridade</InputLabel>
                                    <Select
                                        value={formData.seniority}
                                        label="Senioridade"
                                        labelId="vacancy-seniority-label"
                                        onChange={handleChange('seniority')}
                                    >
                                        {seniorityLevels.map((level) => (
                                            <MenuItem key={level.value} value={level.value}>
                                                {level.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Localização"
                                    value={formData.location}
                                    onChange={handleChange('location')}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Descrição"
                                    multiline
                                    rows={4}
                                    value={formData.description}
                                    onChange={handleChange('description')}
                                    required
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Requisitos"
                                    multiline
                                    rows={4}
                                    value={formData.requirements}
                                    onChange={handleChange('requirements')}
                                    required
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Benefícios"
                                    multiline
                                    rows={4}
                                    value={formData.benefits}
                                    onChange={handleChange('benefits')}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.is_public}
                                            onChange={handleChange('is_public')}
                                        />
                                    }
                                    label="Tornar vaga pública"
                                />
                                <FormHelperText>
                                    Aparece na página pública de vagas quando estiver pública e com status "Aberta"
                                    (altere o status na lista de vagas). O salário não é exibido publicamente.
                                </FormHelperText>
                                {formData.is_public && id && (
                                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                                        <Typography variant="subtitle1" gutterBottom>
                                            Link público da vaga:
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <TextField
                                                fullWidth
                                                value={`${window.location.origin}/vagas/publicas/${id}`}
                                                InputProps={{
                                                    readOnly: true,
                                                }}
                                                size="small"
                                            />
                                            <Button
                                                variant="contained"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(`${window.location.origin}/vagas/publicas/${id}`);
                                                    enqueueSnackbar('Link copiado para a área de transferência', { variant: 'success' });
                                                }}
                                            >
                                                Copiar
                                            </Button>
                                        </Box>
                                    </Box>
                                )}
                            </Grid>

                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                                    <Button
                                        variant="outlined"
                                        onClick={() => navigate('/vagas')}
                                    >
                                        Cancelar
                                    </Button>
                                    <LoadingButton
                                        type="submit"
                                        variant="contained"
                                        loading={loading}
                                    >
                                        {id ? 'Atualizar' : 'Criar'} Vaga
                                    </LoadingButton>
                                </Box>
                            </Grid>
                        </Grid>
                    </form>
                </Paper>
            </Box>
        </Container>
    );
}; 